import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, Search, Settings2, Bell, CalendarPlus, Phone } from "lucide-react";
import SequencesPanel from "@/components/SequencesPanel";

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const MODES = [["email", "Email"], ["sms", "SMS"], ["whatsapp", "WhatsApp"], ["telephone", "Téléphone"]];
const relDate = (iso) => {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return "hier";
  if (days < 30) return `il y a ${days} j`;
  const m = Math.floor(days / 30);
  return m < 12 ? `il y a ${m} mois` : `il y a ${Math.floor(m / 12)} an(s)`;
};

export default function RelationClient() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("sequences");
  const [exchanges, setExchanges] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selPatient, setSelPatient] = useState(params.get("patient") || null);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useState("sms");
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);

  const load = () => api.get("/exchanges").then((r) => setExchanges(r.data || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/patients").then((r) => setPatients(r.data || [])).catch(() => {});
  }, []);

  // Conversations groupées par patient (dernier message d'abord)
  const convos = useMemo(() => {
    const byP = {};
    exchanges.forEach((e) => {
      const pid = e.patient_id;
      if (!pid) return;
      if (!byP[pid]) byP[pid] = { patient_id: pid, patient_nom: e.patient_nom || "—", items: [] };
      byP[pid].items.push(e);
    });
    const list = Object.values(byP).map((c) => {
      c.items.sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
      const last = c.items[c.items.length - 1];
      c.last = last;
      c.attente = !!last?.attente_reponse && !(last?.message_reponse || "").trim();
      return c;
    });
    list.sort((a, b) => ((a.last?.created_at || "") < (b.last?.created_at || "") ? 1 : -1));
    return list;
  }, [exchanges]);

  const nbAttente = convos.filter((c) => c.attente).length;

  const filteredConvos = useMemo(() => {
    if (!q.trim()) return convos;
    const nq = norm(q);
    return convos.filter((c) => norm(c.patient_nom).includes(nq));
  }, [convos, q]);

  const activeConvo = convos.find((c) => c.patient_id === selPatient);
  const activePatient = patients.find((p) => p.id === selPatient);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [activeConvo, selPatient]);

  const openConvo = (pid) => { setSelPatient(pid); const sp = new URLSearchParams(params); sp.set("patient", pid); setParams(sp, { replace: true }); };

  const send = async () => {
    if (!draft.trim() || !selPatient) return;
    setSending(true);
    try {
      await api.post("/exchanges", { patient_id: selPatient, message: draft.trim(), mode, attente_reponse: true });
      setDraft("");
      toast.success("Message enregistré (envoi réel non connecté)");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  // Rappels : patients à relancer / suivi en retard
  const now = new Date();
  const rappels = useMemo(() => {
    return patients.filter((p) => p.statut === "a_relancer" || (p.prochain_suivi && new Date(p.prochain_suivi) <= now))
      .map((p) => ({ ...p, retard: p.prochain_suivi && new Date(p.prochain_suivi) < now }))
      .sort((a, b) => ((a.prochain_suivi || a.dernier_contact || "") < (b.prochain_suivi || b.dernier_contact || "") ? -1 : 1));
  }, [patients]);

  const initials = (nom) => (nom || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="sx-seg">
          <button data-testid="rel-tab-messages" disabled title="Bientôt disponible" className="sx-seg-item" style={{ opacity: 0.45, cursor: "not-allowed" }}>
            Messages{nbAttente > 0 && <span className="ml-1.5 sx-badge sx-badge-amber" style={{ padding: "0 6px" }}>{nbAttente}</span>}
          </button>
          <button data-testid="rel-tab-rappels" disabled title="Bientôt disponible" className="sx-seg-item" style={{ opacity: 0.45, cursor: "not-allowed" }}>
            Rappels{rappels.length > 0 && <span className="ml-1.5 sx-badge sx-badge-blue" style={{ padding: "0 6px" }}>{rappels.length}</span>}
          </button>
          <button data-testid="rel-tab-sequences" onClick={() => setTab("sequences")} className="sx-seg-item" data-active={tab === "sequences"}>Séquences</button>
        </div>
        <button data-testid="rel-modeles" onClick={() => nav("/parametres/messages")} className="sx-btn-secondary"><Settings2 className="w-4 h-4" />Modèles de messages</button>
      </div>

      {tab === "messages" && (
        <div className="sx-card overflow-hidden" style={{ height: "calc(100vh - var(--header-h, 96px) - 48px)" }}>
          <div className="grid h-full" style={{ gridTemplateColumns: "300px 1fr" }}>
            {/* Colonne conversations */}
            <div className="flex flex-col min-h-0" style={{ borderRight: "1px solid var(--divider)" }}>
              <div className="p-3" style={{ borderBottom: "1px solid var(--divider)" }}>
                <div className="flex items-center gap-2 sx-input" style={{ paddingLeft: 10 }}>
                  <Search className="w-4 h-4" style={{ color: "var(--muted-2)" }} />
                  <input data-testid="rel-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un patient…" className="flex-1 bg-transparent outline-none text-[13px]" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {filteredConvos.length === 0 && <div className="p-4 text-[13px]" style={{ color: "var(--faint)" }}>Aucune conversation.</div>}
                {filteredConvos.map((c) => (
                  <button key={c.patient_id} data-testid={`rel-convo-${c.patient_id}`} onClick={() => openConvo(c.patient_id)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors"
                    style={{ borderBottom: "1px solid var(--divider)", background: selPatient === c.patient_id ? "var(--row-selected)" : "", boxShadow: selPatient === c.patient_id ? "inset 3px 0 0 var(--accent)" : "" }}>
                    <span className="inline-flex items-center justify-center rounded-full text-[11px] font-semibold shrink-0" style={{ width: 34, height: 34, background: "var(--bg-warm)", color: "var(--muted)" }}>{initials(c.patient_nom)}</span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-[13px] truncate" style={{ color: "var(--ink)" }}>{c.patient_nom}</span>
                        <span className="text-[10.5px] shrink-0" style={{ color: "var(--muted-2)" }}>{relDate(c.last?.created_at)}</span>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="text-[12px] truncate flex-1" style={{ color: "var(--muted)" }}>{c.last?.message || ""}</span>
                        {c.attente && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "var(--accent)" }} title="En attente de réponse" />}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Colonne fil de discussion */}
            <div className="flex flex-col min-h-0">
              {!selPatient ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                  <MessageSquare className="w-8 h-8 mb-2" style={{ color: "var(--muted-2)" }} />
                  <div className="text-[14px] font-semibold" style={{ color: "var(--ink)" }}>Sélectionnez une conversation</div>
                  <div className="text-[12.5px] mt-1" style={{ color: "var(--muted)" }}>Choisissez un patient à gauche pour voir l'historique des échanges.</div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--divider)" }}>
                    <div className="min-w-0">
                      <button data-testid="rel-open-patient" onClick={() => nav(`/patients/${selPatient}`)} className="font-bold text-[15px] hover:underline" style={{ color: "var(--ink)" }}>{activeConvo?.patient_nom || (activePatient ? `${activePatient.prenom} ${activePatient.nom}` : "Patient")}</button>
                      {activePatient?.telephone && <div className="text-[12px] mono" style={{ color: "var(--muted-2)" }}>{activePatient.telephone}</div>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <a href={activePatient?.telephone ? `tel:${activePatient.telephone}` : undefined} className="sx-icon-btn" style={{ border: "1px solid var(--border-input)", opacity: activePatient?.telephone ? 1 : 0.4 }} title="Appeler"><Phone className="w-4 h-4" /></a>
                    </div>
                  </div>

                  <div ref={threadRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ background: "var(--bg-warm)" }} data-testid="rel-thread">
                    {(activeConvo?.items || []).length === 0 && <div className="text-center text-[13px]" style={{ color: "var(--faint)" }}>Aucun message. Écrivez le premier ci-dessous.</div>}
                    {(activeConvo?.items || []).map((e) => (
                      <div key={e.id}>
                        <div className="flex justify-end">
                          <div className="max-w-[75%] rounded-2xl rounded-br-sm px-3.5 py-2 text-[13px]" style={{ background: "var(--ink)", color: "#fff" }} data-testid={`rel-msg-out-${e.id}`}>
                            <div className="whitespace-pre-wrap">{e.message}</div>
                            <div className="text-[10px] mt-1 text-right" style={{ color: "rgba(255,255,255,0.6)" }}>{(e.mode || "").toUpperCase()} · {new Date(e.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
                          </div>
                        </div>
                        {(e.message_reponse || "").trim() && (
                          <div className="flex justify-start mt-2">
                            <div className="max-w-[75%] rounded-2xl rounded-bl-sm px-3.5 py-2 text-[13px]" style={{ background: "#fff", color: "var(--ink)", border: "1px solid var(--border)" }} data-testid={`rel-msg-in-${e.id}`}>
                              <div className="whitespace-pre-wrap">{e.message_reponse}</div>
                              <div className="text-[10px] mt-1" style={{ color: "var(--muted-2)" }}>Réponse du patient</div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="px-3 py-3" style={{ borderTop: "1px solid var(--divider)" }}>
                    <div className="flex items-end gap-2">
                      <select data-testid="rel-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="sx-input" style={{ width: "auto", height: 38, fontSize: 12.5 }}>
                        {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      <textarea data-testid="rel-draft" value={draft} onChange={(e) => setDraft(e.target.value)} rows={1} placeholder="Écrire un message…"
                        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
                        className="flex-1 rounded-lg px-3 py-2 text-[13px] resize-none" style={{ border: "1px solid var(--border-input)", maxHeight: 120 }} />
                      <button data-testid="rel-send" onClick={send} disabled={!draft.trim() || sending} className="sx-btn-dark" style={{ height: 38, opacity: draft.trim() && !sending ? 1 : 0.5 }}><Send className="w-4 h-4" />Envoyer</button>
                    </div>
                    <div className="text-[10.5px] mt-1.5" style={{ color: "var(--muted-2)" }}>La réception des réponses (entrant) n'est pas encore connectée. Les messages sont enregistrés dans l'historique.</div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "rappels" && (
        <div className="sx-card overflow-hidden">
          <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: "1px solid var(--divider)" }}>
            <Bell className="w-4 h-4" style={{ color: "var(--muted)" }} />
            <span className="text-[13.5px] font-semibold" style={{ color: "var(--ink)" }}>Patients à relancer</span>
            <span className="mono text-[12px]" style={{ color: "var(--muted-2)" }}>{rappels.length}</span>
          </div>
          {rappels.length === 0 ? (
            <div className="px-4 py-12 text-center text-[13px]" style={{ color: "var(--faint)" }}>Aucun rappel en attente. 🎉</div>
          ) : (
            <table className="w-full text-[13.5px]">
              <tbody>
                {rappels.map((p) => (
                  <tr key={p.id} data-testid={`rappel-row-${p.id}`} style={{ borderTop: "1px solid var(--divider)" }}>
                    <td className="px-4 py-2.5">
                      <button onClick={() => nav(`/patients/${p.id}`)} className="font-semibold hover:underline" style={{ color: "var(--ink)" }}>{p.prenom} {p.nom}</button>
                      <div className="mono text-[10.5px]" style={{ color: "var(--id)" }}>{p.id}</div>
                    </td>
                    <td className="px-4 py-2.5" style={{ color: "var(--muted)" }}>
                      {p.retard
                        ? <span className="sx-badge sx-badge-red">Suivi en retard · {new Date(p.prochain_suivi).toLocaleDateString("fr-FR")}</span>
                        : <span className="sx-badge sx-badge-amber">Dernier contact {relDate(p.dernier_contact)}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <button data-testid={`rappel-msg-${p.id}`} onClick={() => { setTab("messages"); openConvo(p.id); }} className="sx-btn-secondary" style={{ height: 30 }}><MessageSquare className="w-3.5 h-3.5" />Message</button>
                        <button data-testid={`rappel-rdv-${p.id}`} onClick={() => nav(`/patients/${p.id}`)} className="sx-btn-secondary" style={{ height: 30 }}><CalendarPlus className="w-3.5 h-3.5" />Fiche</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {tab === "sequences" && <SequencesPanel />}
    </div>
  );
}
